import React, { useState, useEffect, useCallback } from "react";
import { ListItemText, Box, Typography, List, Collapse, useTheme } from "@mui/material";
import { ExpandLess, ExpandMore, Settings } from "@mui/icons-material";
import { NavLink, useNavigate } from "react-router-dom";
import "../assets/style.css";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from '../state/store';
import { setLoadingState, setNeedHomeRedirect, setPage } from "../state/appSlice";
import { useTypedSelector } from "../hooks/useTypedSelector";
import { PackageTypeUnassigned, PANEL_IDS } from "../utils/constants";
import { useHasUnsavedChanges } from "../hooks/useHasUnsavedChanges";
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UnsavedChangesDialog from "../components/shared/config/UnsavedChangesDialog";
import { useSaveAndContinue } from "../hooks/useSaveAndContinue";
import { useHostDetails } from "../hooks/useHostDetails";
import { useDiscardAndContinue } from "../hooks/useDiscardAndContinue";

const SideBar: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { host } = useHostDetails();
    const appState = useSelector((state: RootState) => state.appSlice)
    const packages = useTypedSelector((state) => state.configPlugInData.data?.Configuration.Packages);
    const controlsState = useSelector((state: RootState) => state.configPlugInData.data?.Configuration.OtherControls)
    const needSetupRedirect = useTypedSelector((state) => state.appSlice.needSetupRedirect);
    const needHomeRedirect = useTypedSelector((state) => state.appSlice.needHomeRedirect);
    const [sidebarLinks, setSidebarLinks] = useState<Array<{ 'name': string, 'path': string }>>([]);
    const [showConfigLink, setShowConfigLink] = useState(true);
    const [currentMenu, setCurrentMenu] = useState<'home' | 'configuration' | 'pushback'>('home');
    const [showPushbackSubmenu, setShowPushbackSubmenu] = useState(false);
    const [showMiscSubmenu, setShowMiscSubmenu] = useState(false);
    const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
    const [saveErrorMessage, setSaveErrorMessage] = useState("");

    const hasUnsavedChanges = useHasUnsavedChanges();

    const configurationLinks = [
        { name: 'Servers', path: './configuration/ConfigServers' },
        { name: 'Packages', path: './configuration/ConfigPackages' },
        { name: 'Delivery Methods', path: './configuration/ConfigDeliveryMethods' },
        { name: 'Other Controls', path: './configuration/ConfigOtherControls' },
        { name: 'Send Validations', path: './configuration/ConfigSendValidations' },
        { name: 'Response Messages', path: './configuration/ConfigResponseMessages' },
    ];

    const miscLinks = [
        { name: 'Configuration', path: './configuration/ConfigMisc' },
        { name: 'Evaluation Result', path: './configuration/ConfigMiscResult' },
    ];

    const pushbackLinks = [
        { name: 'Dashboard', path: './pushback/Dashboard' },
        { name: 'Doc Bundling', path: './configuration/ConfigBundling' },
        { name: 'Xpath Mappings', path: './pushback/XpathMappings' },
        { name: 'Configuration', path: './configuration/ConfigPushback' }
    ];

    const setCurrentPage = useCallback((page: string, menu: 'home' | 'configuration' | 'pushback' = 'home') => {
        document.getElementsByClassName("sideNavNavPage")[0]?.scrollTo(0,0);
        dispatch(setPage(page));
        setCurrentMenu(menu);
        
        // Reset submenu states when navigating to home
        if (menu === 'home') {
            setShowPushbackSubmenu(false);
            setShowMiscSubmenu(false);
        }
    }, [dispatch]);

    useEffect(() => {
        let sideGroupsList: Array<{ 'name': string, 'path': string }> = [];
        const packageSort = packages?.map(pkg => ({ ...pkg })).sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0)) ?? [];
        if (packageSort) {
            for (let i = 0; i < packageSort.length; i++) {
                if (!sideGroupsList.some(g => g.name === packageSort[i].Group) && packageSort[i].Group?.toLowerCase() !== PackageTypeUnassigned.toLowerCase())
                    sideGroupsList.push({ 'name': packageSort[i].Group, 'path': './Order' });
            }
            setSidebarLinks(sideGroupsList);
            const isCurrentPageValid = sideGroupsList.some(link => link.name === appState.page);
            if (packageSort.length > 0 && (appState.page === '/' || !appState.page || (!isCurrentPageValid && currentMenu === 'home'))) {
                dispatch(setPage(sideGroupsList[0]?.name ?? ""));
                navigate("/Order");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packages])

    useEffect(() => {
        if (needSetupRedirect) { //Router is silly. Can't redirect from App.tsx
            setCurrentPage("Servers", "configuration");
            dispatch(setLoadingState({ loading:false }));
            navigate("/configuration/ConfigServers");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needSetupRedirect])

    //Redirect to Home page with current event as it is dynamic for every loan, 
    //not possible from component level
    useEffect(() => {
        // Wait for sidebarLinks so the first page name is resolvable, then clear the
        // flag here at the point of consumption. Leaving it set re-fires this effect
        // whenever sidebarLinks is rebuilt, force-navigating away from the current page.
        if (needHomeRedirect && sidebarLinks.length > 0) {
            setCurrentPage(sidebarLinks[0]?.name || '', 'home');
            navigate("/Order");
            dispatch(setNeedHomeRedirect(false));
        }
    }, [needHomeRedirect, navigate, sidebarLinks, setCurrentPage, dispatch])

    // Listen for home navigation event from header
    useEffect(() => {
        const handleNavigateToHome = () => {
            if (sidebarLinks.length > 0) {
                setCurrentMenu('home');
                setShowPushbackSubmenu(false);
                setShowMiscSubmenu(false);
                setCurrentPage(sidebarLinks[0]?.name || '', 'home');
                navigate("/Order");
            }
        };

        window.addEventListener('navigateToHome', handleNavigateToHome);
        return () => {
            window.removeEventListener('navigateToHome', handleNavigateToHome);
        };
    }, [sidebarLinks, navigate, setCurrentPage])

    useEffect(() => {
        const configControlOpts = controlsState?.find(c => c.Id === PANEL_IDS.CONFIGURATION);
        // Ensure Configuration link is always visible for Admin, even when OtherControls(controlsState) is empty.
        // This allows Admin to import configuration, hence the fallback value is set to true.
        // Hide configuration section for all TPO Connect users (Admin or non Admin)
        setShowConfigLink(appState.isAdmin && (configControlOpts?.Enabled ?? true) && !host.isTpoConnect); 
    }, [appState.isAdmin, controlsState, host.isTpoConnect])

    // Styles
    const navLinkStyle = ({ isActive }: { isActive: boolean }) => ({
        textDecoration: "none",
        color: theme.palette.primary.contrastText,
        fontWeight: isActive ? 600 : 400,
        fontSize: "0.95rem",
        display: "flex",
        alignItems: "center",
        borderRadius: 8,
        background: isActive ? theme.palette.primary.main : "none",
        transition: "background 0.2s",
        padding: "8px 18px",
        marginBottom: 4,
        "&:hover": {
            background: isActive ? theme.palette.primary.main : theme.palette.action.hover,
            color: theme.palette.primary.contrastText,
        }
    });

    const subNavLinkStyle = ({ isActive }: { isActive: boolean }) => ({
        ...navLinkStyle({ isActive }),
        paddingLeft: 40,
        fontWeight: isActive ? 600 : 400,
        fontSize: "0.9rem"
    });

    const expanderStyle = () => ({
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        borderRadius: 8,
        px: "18px",
        py: "8px",
        mb: 1,
        fontWeight: 500,
        color: theme.palette.text.primary,
        background: "none",
        "&:hover": {
            background: theme.palette.action.hover,
        }
    });

    const navigateToHome = useCallback(() => {
        if (sidebarLinks.length > 0) {
            setCurrentPage(sidebarLinks[0].name, 'home');
            navigate("/Order");
        }
    }, [navigate, setCurrentPage, sidebarLinks]);

    const handleSectionSwitch = (section: 'home' | 'configuration', event?: React.MouseEvent<HTMLAnchorElement>) => {
        if (section === "home" && currentMenu !== "home" && hasUnsavedChanges) {
            event?.preventDefault();
            setSaveErrorMessage("");
            setUnsavedDialogOpen(true);
            return;
        }
        if (section === "home") {
            navigateToHome();
        }
        if (section === "configuration") {
            setCurrentPage("Servers", "configuration");
            navigate("/configuration/ConfigServers");
        }
    };

    const { handleSaveAndContinue, savingAndContinue } = useSaveAndContinue({
        navigateToHome,
        setUnsavedDialogOpen,
        onError: (message) => {
            setSaveErrorMessage(message);
        }
    });

    const handleDiscardAndContinue = useDiscardAndContinue({
        navigateToHome,
        setUnsavedDialogOpen
    });

    return (
        <Box
            sx={{
                background: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Main Section */}
            <Box sx={{ overflowY: 'auto', flexGrow: 1, minHeight: 0 }}>
                {currentMenu === 'home' && (
                    <List component="nav" sx={{ px: 1, pt: 2 }}>
                        {sidebarLinks.map((link, index) => (
                            <NavLink
                                key={index}
                                to={link.path}
                                style={() => navLinkStyle({ isActive: appState.page === link.name })}
                                onClick={() => setCurrentPage(link.name, 'home')}
                            >
                                <ListItemText 
                                    primary={
                                        <Typography sx={{ fontWeight: 500, letterSpacing: 0.5 }}>
                                            {link.name}
                                        </Typography>
                                    } 
                                />
                            </NavLink>
                        ))}
                    </List>
                )}

                {currentMenu === 'configuration' && (
                    <List sx={{ px: 1, pt: 2 }}>
                        {configurationLinks.map((link, index) => (
                            <NavLink
                                key={index}
                                to={link.path}
                                style={navLinkStyle}
                                onClick={() => setCurrentPage(link.name, 'configuration')}
                            >
                                <ListItemText 
                                    primary={
                                        <Typography sx={{ fontWeight: 500, letterSpacing: 0.5 }}>
                                            {link.name}
                                        </Typography>
                                    } 
                                />
                            </NavLink>
                        ))}

                        {/* Pushback Expander */}
                        <Box
                            sx={expanderStyle()}
                            onClick={() => {
                                setShowPushbackSubmenu(!showPushbackSubmenu);
                                setCurrentPage('Pushback', 'configuration');
                            }}
                        >
                            <ListItemText primary="Pushback" sx={{ color: theme.palette.primary.contrastText }} />
                            {showPushbackSubmenu ? 
                                <ExpandLess sx={{ color: theme.palette.primary.contrastText }} /> : 
                                <ExpandMore sx={{ color: theme.palette.primary.contrastText }} />
                            }
                        </Box>
                        <Collapse in={showPushbackSubmenu} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {pushbackLinks.map((link, index) => (
                                    <NavLink
                                        key={`pushback-${index}`}
                                        to={link.path}
                                        style={subNavLinkStyle}
                                        onClick={() => setCurrentPage(link.name, 'configuration')}
                                    >
                                        <ListItemText 
                                            primary={
                                                <Typography sx={{ fontWeight: 500, letterSpacing: 0.5 }}>
                                                    {link.name}
                                                </Typography>
                                            } 
                                        />
                                    </NavLink>
                                ))}
                            </List>
                        </Collapse>

                        {/* Misc Expander */}
                        <Box
                            sx={expanderStyle()}
                            onClick={() => {
                                setShowMiscSubmenu(!showMiscSubmenu);
                                setCurrentPage('Misc', 'configuration');
                            }}
                        >
                            <ListItemText primary="Misc" sx={{ color: theme.palette.primary.contrastText }} />
                            {showMiscSubmenu ? 
                                <ExpandLess sx={{ color: theme.palette.primary.contrastText }} /> : 
                                <ExpandMore sx={{ color: theme.palette.primary.contrastText }} />
                            }
                        </Box>
                        <Collapse in={showMiscSubmenu} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {miscLinks.map((link, index) => (
                                    <NavLink
                                        key={`misc-${index}`}
                                        to={link.path}
                                        onClick={() => setCurrentPage(link.name, 'configuration')}
                                        tabIndex={link.name !== 'Configuration' ? -1 : 0}
                                        aria-disabled={link.name !== 'Configuration'}
                                        style={({ isActive }) => ({
                                            ...subNavLinkStyle({ isActive }),
                                            ...(link.name !== 'Configuration'
                                                ? { opacity: 0.5, pointerEvents: "none" }
                                                : {})
                                        })}
                                    >
                                        <ListItemText 
                                            primary={
                                                <Typography sx={{ fontWeight: 500, letterSpacing: 0.5 }}>
                                                    {link.name}
                                                </Typography>
                                            } 
                                            sx={{ 
                                                opacity: link.name !== 'Configuration' ? 0.5 : 1
                                            }} 
                                        />
                                    </NavLink>
                                ))}
                            </List>
                        </Collapse>
                    </List>
                )}
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Section Switcher */}
            {showConfigLink && (
                <Box
                    sx={{
                        position: "sticky",
                        left: 0,
                        bottom: 0,
                        background: theme.palette.primary.main,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        py: 1,
                        px: 2,
                        zIndex: 2,
                    }}
                >
                    <NavLink
                        to={currentMenu === "home" ? configurationLinks[0].path : sidebarLinks[0]?.path || "/Order"}
                        onClick={(event) => handleSectionSwitch(currentMenu === "home" ? "configuration" : "home", event)}
                        style={{
                            position: "sticky",
                            left: 0,
                            bottom: 0,
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            zIndex: 2,
                            width: "100%",
                            textDecoration: "none",
                            gap: "8px",
                            color: theme.palette.primary.contrastText,
                        }}
                    >
                        <SwapHorizIcon />
                        <ListItemText
                            primary={
                                <Typography sx={{ fontWeight: 500, letterSpacing: 0.5, textAlign: 'left' }}>
                                    {currentMenu === "home" ? "Go To Configuration" : "Go To Home"}
                                </Typography>
                            }
                        />
                    </NavLink>
                </Box>
            )}
            <UnsavedChangesDialog
                open={unsavedDialogOpen}
                onSaveAndContinue={() => {
                    setSaveErrorMessage("");
                    handleSaveAndContinue();
                }}
                onDiscard={() => {
                    setSaveErrorMessage("");
                    handleDiscardAndContinue();
                }}
                onCancel={() => {
                    setSaveErrorMessage("");
                    setUnsavedDialogOpen(false);
                }}
                loading={savingAndContinue}
                errorMessage={saveErrorMessage}
            />
        </Box>
    );
};

export default SideBar;